/**
 * Exporta seções para CSV — uma seção por arquivo.
 *
 *  - UTF-8 com BOM (Excel pt-BR abre sem mojibake).
 *  - Separador `;` (padrão Excel pt-BR).
 *  - Aspas duplas em campos com `;`, aspas ou quebra.
 *  - Datas formatadas como `dd/MM/yyyy`, moeda como `1234,56`.
 */

import { displayCell, slugifyFilename, timestampForFilename } from "./formatters";

const BOM = "﻿";

function csvEscape(value) {
  if (value == null) return "";
  const s = String(value);
  if (/[";\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function gerarConteudoCSV(secao) {
  const { columns, rows } = secao;
  const linhas = [];
  linhas.push(columns.map((c) => csvEscape(c.label)).join(";"));
  for (const r of rows || []) {
    linhas.push(
      columns
        .map((c) => csvEscape(displayCell(r[c.key], c.type || "text")))
        .join(";")
    );
  }
  return BOM + linhas.join("\r\n");
}

function downloadBlob(content, filename, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Gera 1 CSV por seção. Se houver mais de uma seção, o navegador baixa
 * múltiplos arquivos (cada um nomeado com o id da seção).
 *
 * @param {Object} opts
 * @param {string} opts.titulo
 * @param {Array<{ id, label, columns, rows }>} opts.secoes
 */
export function salvarCSV(opts) {
  const { titulo = "Exportação", secoes = [] } = opts;
  const ts = timestampForFilename();
  const tituloSlug = slugifyFilename(titulo);
  const arquivos = [];
  for (const secao of secoes) {
    const conteudo = gerarConteudoCSV(secao);
    const sufixo = secao.id ? `_${slugifyFilename(secao.id)}` : "";
    const filename = `${tituloSlug}${sufixo}_${ts}.csv`;
    downloadBlob(conteudo, filename);
    arquivos.push(filename);
  }
  return arquivos;
}

/**
 * Gera Blobs CSV (um por seção) — sem fazer download.
 * Retorna Array<{ blob, filename }>.
 */
export function gerarBlobsCSV(opts) {
  const { titulo = "Exportação", secoes = [] } = opts;
  const ts = timestampForFilename();
  const tituloSlug = slugifyFilename(titulo);
  const out = [];
  for (const secao of secoes) {
    const conteudo = gerarConteudoCSV(secao);
    const sufixo = secao.id ? `_${slugifyFilename(secao.id)}` : "";
    const filename = `${tituloSlug}${sufixo}_${ts}.csv`;
    const blob = new Blob([conteudo], { type: "text/csv;charset=utf-8" });
    out.push({ blob, filename });
  }
  return out;
}

// Exportado também para testes.
export const __test__ = { gerarConteudoCSV, csvEscape };
